import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL || '';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
});

// Auth interceptor - attach accessToken to requests
api.interceptors.request.use((config) => {
  const accessToken = sessionStorage.getItem('accessToken');
  if (accessToken) {
    config.params = { ...config.params, auth: accessToken };
  }
  return config;
});

// Response interceptor - clear stale auth on 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      sessionStorage.removeItem('accessToken');
    }
    return Promise.reject(error);
  },
);

// Matches
export const getMatches = () => api.get('/api/matches/');
export const getMatchesPublic = () => axios.get(`${BASE_URL}/api/matches/`);
export const getMatchesForShooter = (publicId) => api.get('/api/matches/', { params: { mode: 'as-shooter-or-staff', publicId } });
export const getMatch = (matchId) => api.get(`/api/matches/${matchId}`);
export const createMatch = (data) => api.post('/api/matches/', data);
export const updateMatch = (matchId, data, params) => api.patch(`/api/matches/${matchId}`, data, { params });
export const deleteMatch = (matchId) => api.delete(`/api/matches/${matchId}`);
export const getMatchAuth = (matchId) => api.get(`/api/matches/${matchId}/auth`);
export const getMatchLogins = (matchId) => api.get(`/api/matches/${matchId}/logins`);
export const getMatchStaff = (matchId) => api.get(`/api/matches/${matchId}/staff`);
export const updateMatchStaff = (matchId, data) => api.put(`/api/matches/${matchId}/staff`, { staff: data });
export const uploadMatchImage = (matchId, formData) => api.post(`/api/matches/${matchId}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// Shooters
export const getShooters = (matchId, params) => api.get(`/api/matches/${matchId}/shooters/`, { params });
export const getMyShooters = (matchId, params) => api.get(`/api/matches/${matchId}/shooters/mine`, { params });
export const getShooter = (matchId, shooterId) => api.get(`/api/matches/${matchId}/shooters/${shooterId}`);
export const verifyPin = (matchId, data) => api.post(`/api/matches/${matchId}/shooters/pin`, data);
export const forgotPin = (matchId, data) => api.post(`/api/matches/${matchId}/shooters/forgot-pin`, data);
export const registerShooter = (matchId, data, params) => api.post(`/api/matches/${matchId}/shooters/`, data, { params });
export const updateShooter = (matchId, shooterId, data, params) => api.patch(`/api/matches/${matchId}/shooters/${shooterId}`, data, { params });
export const deleteShooter = (matchId, shooterId) => api.delete(`/api/matches/${matchId}/shooters/${shooterId}`);
export const getSquadAvailability = (matchId, params) => api.get(`/api/matches/${matchId}/squads/availability`, { params });
export const checkShooterId = (matchId, publicId) => api.get(`/api/matches/${matchId}/shooters/checkId/${publicId}`);
export const updateShooterComments = (matchId, shooterId, data) => api.put(`/api/matches/${matchId}/shooters/${shooterId}/comments`, data);
export const submitShooterChrono = (matchId, shooterId, data) => api.post(`/api/matches/${matchId}/shooters/${shooterId}/chrono`, data);
export const submitEquipmentCheck = (matchId, data) => api.post(`/api/matches/${matchId}/shooters/equipmentCheck`, data);
export const getShooterPayment = (matchId, shooterId) => api.get(`/api/matches/${matchId}/shooters/${shooterId}/payment`);

// Stages
export const getStages = (stageIds) => api.get(`/api/stages/${stageIds}`);
export const createStage = (data) => api.post('/api/stages/', data);
export const updateStage = (stageId, data, params) => api.put(`/api/stages/${stageId}/`, data, { params });
export const uploadStageImage = (stageId, formData) => api.post(`/api/stages/${stageId}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const addStageToMatch = (matchId, stageId) => api.post(`/api/matches/${matchId}/stages/`, { stageId });

// Scores
export const getScores = (matchId, params) => api.get(`/api/matches/${matchId}/scores/`, { params });
export const submitScore = (matchId, data, { finalize } = {}) => api.post(`/api/matches/${matchId}/scores/`, data, { params: finalize ? { finalize: 'true' } : {} });
export const updateScore = (matchId, scoreId, data) => api.patch(`/api/matches/${matchId}/scores/${scoreId}`, data);
export const deleteScore = (matchId, scoreId) => api.delete(`/api/matches/${matchId}/scores/${scoreId}`);
export const getSignature = (matchId, signatureId) => api.get(`/api/matches/${matchId}/scores/signatures/${signatureId}`);

// Messages
export const getMessages = (matchId) => api.get(`/api/matches/${matchId}/messages/`);
export const sendMessage = (matchId, data) => api.post(`/api/matches/${matchId}/messages/`, data);
export const markMessagesRead = (matchId, data) => api.post(`/api/matches/${matchId}/messages/read`, data);

// Announcements
export const getAnnouncements = (matchId) => api.get(`/api/matches/${matchId}/announcements/`);
export const postAnnouncement = (matchId, data) => api.post(`/api/matches/${matchId}/announcements/`, data);
export const deleteAnnouncement = (matchId, announcementId) => api.delete(`/api/matches/${matchId}/announcements/${announcementId}`);

// Chronos
export const getChronos = (matchId) => api.get(`/api/matches/${matchId}/chronos/`);
export const setChronoGun = (matchId, stationId, data) => api.post(`/api/matches/${matchId}/chronos/${stationId}/gun`, data);
export const addChronoDaily = (matchId, stationId, data) => api.post(`/api/matches/${matchId}/chronos/${stationId}/daily`, data);
export const addChronoRequest = (matchId, stationId, data) => api.post(`/api/matches/${matchId}/chronos/${stationId}/requests`, data);
export const postShooterChrono = (matchId, shooterId, data) => api.post(`/api/matches/${matchId}/shooters/${shooterId}/chrono`, data);

// Logins
export const getLogin = () => api.get('/api/logins/');
export const getLoginById = (loginId) => api.get(`/api/logins/${loginId}`);
export const createLogin = (data) => api.post('/api/logins/', data);
export const staffAuth = (matchId) => api.post('/api/logins/staff-auth', { matchId });
export const staffCodeAuth = (matchId, code) => api.post('/api/logins/staff-code', { matchId, code });
export const staffGoogleAuth = (matchId, googleToken) => api.post('/api/logins/staff-google', { matchId, googleToken });
export const getMyShootersAll = (params) => api.get('/api/logins/my-shooters', { params });
export const uploadProfileImage = (formData) => api.post('/api/logins/profile-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const uploadMedia = (matchId, formData) => api.post(`/api/logins/media/${matchId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// Clubs
export const getClubs = (region) => api.get('/api/clubs/', { params: { region } });
export const getClub = (clubId) => api.get(`/api/clubs/${clubId}`);
export const createClub = (data) => api.post('/api/clubs/', data);
export const getClubShooters = (clubId) => api.get(`/api/clubs/${clubId}/shooters`);
export const addClubShooter = (clubId, data) => api.post(`/api/clubs/${clubId}/shooters/`, data);
export const deleteClubShooter = (clubId, shooterId) => api.delete(`/api/clubs/${clubId}/shooters/${shooterId}`);
export const getClubAuth = (clubId) => api.get(`/api/clubs/${clubId}/auth`);

// Regions
export const getRegion = (regionId) => api.get(`/api/regions/${regionId}`);
export const getRegionAuth = (regionId) => api.get(`/api/regions/${regionId}/auth`);

// Leagues
export const getLeague = (leagueId) => api.get(`/api/leagues/${leagueId}`);
export const createLeague = (league) => api.post('/api/leagues/', { league });

// Push Notifications
export const registerPushToken = (data) => api.post('/api/push-notifications/', data);

// State Storage
export const saveStateHistory = (data) => api.post('/api/state-storage/history/', data);

// Agent
export const checkAgentAccess = () => api.get('/api/agent/access');
export const createAgentConversation = (matchId) => api.post('/api/agent/conversations', { matchId });
export const sendAgentMessage = (conversationId, data) => api.post(`/api/agent/conversations/${conversationId}/messages`, data);
export const getAgentConversation = (conversationId) => api.get(`/api/agent/conversations/${conversationId}`);
export const listAgentConversations = (matchId) => api.get('/api/agent/conversations', { params: { matchId } });
export const deleteAgentConversation = (conversationId) => api.delete(`/api/agent/conversations/${conversationId}`);
export const getAgentApprovedUsers = () => api.get('/api/agent/approved-users');
export const approveAgentUser = (data) => api.post('/api/agent/approved-users', data);
export const revokeAgentUser = (userId) => api.delete(`/api/agent/approved-users/${encodeURIComponent(userId)}`);

// Utils
export const submitFeedback = (data) => api.post('/api/utils/feedback', data);
export const duplicateDemo = ({ demoId }) => api.get(`/api/utils/duplicate-demo?demoId=${demoId}`);

export default api;
